import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { FilmThumbnail } from '@/components/common/FilmThumbnail'
import type { FilmDocument } from '@/types'

interface HeroSectionProps {
  latestFilm: (FilmDocument & { id: string }) | null
  loading: boolean
}

export function HeroSection({ latestFilm, loading }: HeroSectionProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-brand-orange-light to-background dark:from-brand-orange-dim">
      <div className="container mx-auto px-4 py-24 md:py-32">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:items-center">
          {/* Copy */}
          <div className="space-y-6 max-w-xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange">
              Our Mission
            </p>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl leading-tight">
              Dance. Film. Community.
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              At One Day Dance, we believe dance has the power to connect people across cultures and
              communities. We inspire, entertain, and educate through innovative dance films
              accessible to a worldwide audience.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/seasons">
                <Button size="lg">Watch Our Films</Button>
              </Link>
              <Link to="/applications">
                <Button size="lg" variant="outline">Apply Now</Button>
              </Link>
            </div>
          </div>

          {/* Latest film card */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-blue-dark">
              Latest Film
            </p>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="aspect-video w-full rounded-lg" />
                <Skeleton className="h-5 w-48" />
              </div>
            ) : latestFilm ? (
              <Link to={`/films/${latestFilm.slug}`} className="group block">
                <div className="relative overflow-hidden rounded-lg bg-black pt-[56.25%]">
                  <FilmThumbnail
                    title={latestFilm.title}
                    thumbnailUrl={latestFilm.thumbnailUrl}
                    youtubeId={latestFilm.youtubeId}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/30" />
                  {(latestFilm.type ?? 'episode') === 'episode' && (
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      <Badge className="bg-brand-orange text-brand-black border-0">S{latestFilm.seasonNumber} EP{latestFilm.episodeNumber}</Badge>
                    </div>
                  )}
                </div>
                <p className="mt-2 font-semibold group-hover:text-primary transition-colors">
                  {latestFilm.title}
                </p>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
